import { Link } from "@tanstack/react-router";
import {
  BadgeCheck,
  Flame,
  LifeBuoy,
  ListChecks,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
  Store,
  Wallet,
} from "lucide-react";
import { ContactForm } from "@/components/contact-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLocale, useT } from "@/i18n";
import { getMpHowItWorksContent } from "@/lib/marketplace/how-it-works-content.i18n";
import {
  MP_BUYER_FEE_RATE,
  MP_EARNINGS_PENDING_DAYS,
  MP_REVIEW_HOURS,
  MP_SELLER_FEE_RATE,
} from "@/lib/marketplace/content";

export function MarketplaceHowItWorks() {
  const { t } = useT();
  const locale = useLocale();
  const content = getMpHowItWorksContent(locale);

  const sellerFee = `${Math.round(MP_SELLER_FEE_RATE * 100)}%`;
  const buyerFee = `${Math.round(MP_BUYER_FEE_RATE * 100)}%`;

  const MP_TOC = [
    ["mp-buying", t("mphiw.toc.buying")],
    ["mp-selling", t("mphiw.toc.selling")],
    ["mp-featuring", t("mphiw.toc.featuring")],
    ["mp-protection", t("mphiw.toc.protection")],
    ["mp-fees", t("mphiw.toc.fees")],
    ["mp-rules", t("mphiw.toc.rules")],
    ["mp-glossary", t("mphiw.toc.glossary")],
    ["mp-faq", t("mphiw.toc.faq")],
    ["mp-contact", t("mphiw.toc.contact")],
  ] as const;

  const PROTECTION = [
    {
      icon: Wallet,
      title: t("mphiw.protection.wallet_title"),
      body: t("mphiw.protection.wallet_body"),
    },
    {
      icon: ShieldCheck,
      title: t("mphiw.protection.protected_title"),
      body: t("mphiw.protection.protected_body"),
    },
    {
      icon: BadgeCheck,
      title: t("mphiw.protection.verified_title"),
      body: t("mphiw.protection.verified_body"),
    },
    {
      icon: LifeBuoy,
      title: t("mphiw.protection.dispute_title"),
      body: t("mphiw.protection.dispute_body"),
    },
    {
      icon: ListChecks,
      title: t("mphiw.protection.logged_title"),
      body: t("mphiw.protection.logged_body"),
    },
    {
      icon: Star,
      title: t("mphiw.protection.reviews_title"),
      body: t("mphiw.protection.reviews_body"),
    },
  ];

  return (
    <div>
      <nav className="mt-8" aria-label={t("mphiw.toc.aria_label")}>
        <h2 className="sr-only">{t("mphiw.toc.title_sr")}</h2>
        <ul className="flex flex-wrap gap-2">
          {MP_TOC.map(([id, label]) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className="inline-flex rounded-full border border-border px-3 py-1 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <p className="mt-8 max-w-2xl text-muted-foreground">{content.intro}</p>

      <section className="mt-12" id="mp-buying" aria-labelledby="mp-buying-heading">
        <div className="flex items-center gap-2">
          <Search className="size-5 text-primary" aria-hidden="true" />
          <h2 id="mp-buying-heading" className="font-display text-2xl font-bold">
            {t("mphiw.buying.heading")}
          </h2>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <ol className="contents list-none">
            {content.buyerSteps.map((s) => (
              <li key={s.title} className="card-surface p-5">
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="card-surface flex flex-col justify-center p-5">
            <h3 className="font-display text-lg font-bold">{t("mphiw.buying.ready_title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("mphiw.buying.ready_body")}</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Button asChild variant="hot" className="w-full sm:w-auto">
                <Link to="/marketplace">{t("mphiw.buying.browse_cta")}</Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/marketplace/requests">{t("mphiw.buying.post_request_cta")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-14" id="mp-selling" aria-labelledby="mp-selling-heading">
        <div className="flex items-center gap-2">
          <Store className="size-5 text-primary" aria-hidden="true" />
          <h2 id="mp-selling-heading" className="font-display text-2xl font-bold">
            {t("mphiw.selling.heading")}
          </h2>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <ol className="contents list-none">
            {content.sellerSteps.map((s) => (
              <li key={s.title} className="card-surface p-5">
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="card-surface flex flex-col justify-center p-5">
            <h3 className="font-display text-lg font-bold">{t("mphiw.selling.start_title")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("mphiw.selling.start_body")}</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Button asChild variant="hot" className="w-full sm:w-auto">
                <Link to="/marketplace/sell">{t("mphiw.selling.sell_cta")}</Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/marketplace/services/new">{t("mphiw.selling.create_cta")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-14" id="mp-featuring" aria-labelledby="mp-featuring-heading">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles className="size-5 text-primary" aria-hidden="true" />
          <h2 id="mp-featuring-heading" className="font-display text-2xl font-bold">
            {t("mphiw.featuring.heading")}
          </h2>
          <Badge variant="outline">{t("mphiw.featuring.free_badge")}</Badge>
        </div>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">{content.featuring.intro}</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <div className="card-surface p-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("mphiw.featuring.publish_label")}
            </p>
            <p className="mt-1 font-display text-2xl font-bold text-primary">
              {t("mphiw.featuring.publish_price")}
            </p>
            <p className="text-xs text-muted-foreground">{t("mphiw.featuring.publish_note")}</p>
          </div>
          {content.featuring.plans.map((p) => (
            <div key={p.label} className="card-surface p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t("mphiw.featuring.featured_label_prefix")} {p.label}
              </p>
              <p className="mt-1 font-display text-2xl font-bold">
                {p.heat} {t("mphiw.featuring.heat_suffix")}
              </p>
              <p className="text-xs text-muted-foreground">{p.blurb}</p>
            </div>
          ))}
        </div>
        <div className="card-surface mt-4 p-6">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {content.featuring.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-14" id="mp-protection" aria-labelledby="mp-protection-heading">
        <h2 id="mp-protection-heading" className="font-display text-2xl font-bold">
          {t("mphiw.protection.heading")}
        </h2>
        <ul className="mt-5 grid list-none gap-4 sm:grid-cols-2">
          {PROTECTION.map((p) => (
            <li key={p.title} className="card-surface p-5">
              <p.icon className="size-5 text-primary" aria-hidden="true" />
              <h3 className="mt-3 font-display text-lg font-bold">{p.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{p.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14" id="mp-fees" aria-labelledby="mp-fees-heading">
        <h2 id="mp-fees-heading" className="font-display text-2xl font-bold">
          {t("mphiw.fees.heading")}
        </h2>
        <div className="card-surface mt-5 grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("mphiw.fees.seller_label")}
            </p>
            <p className="mt-1 font-display text-2xl font-bold text-primary">{sellerFee}</p>
            <p className="text-xs text-muted-foreground">{t("mphiw.fees.seller_note")}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("mphiw.fees.buyer_label")}
            </p>
            <p className="mt-1 font-display text-2xl font-bold">{buyerFee}</p>
            <p className="text-xs text-muted-foreground">{t("mphiw.fees.buyer_note")}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("mphiw.fees.publish_label")}
            </p>
            <p className="mt-1 font-display text-2xl font-bold">{t("mphiw.fees.publish_price")}</p>
            <p className="text-xs text-muted-foreground">
              {t("mphiw.fees.featuring_note", { heat: content.featuring.plans[0]?.heat ?? 10 })}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("mphiw.fees.earnings_label")}
            </p>
            <p className="mt-1 font-display text-2xl font-bold">
              {t("mphiw.fees.earnings_value", { days: MP_EARNINGS_PENDING_DAYS })}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("mphiw.fees.moderation_note", { hours: MP_REVIEW_HOURS })}
            </p>
          </div>
        </div>
      </section>

      <section className="mt-14" id="mp-rules" aria-labelledby="mp-rules-heading">
        <h2 id="mp-rules-heading" className="font-display text-2xl font-bold">
          {t("mphiw.rules.heading")}
        </h2>
        <div className="card-surface mt-5 p-6">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {content.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-14" id="mp-glossary" aria-labelledby="mp-glossary-heading">
        <h2 id="mp-glossary-heading" className="font-display text-2xl font-bold">
          {t("mphiw.glossary.heading")}
        </h2>
        <dl className="mt-5 grid gap-3 sm:grid-cols-2">
          {content.glossary.map(([term, def]) => (
            <div key={term} className="card-surface p-4">
              <dt className="flex items-center gap-1.5 font-display font-bold">
                <Flame className="size-4 text-primary" aria-hidden="true" /> {term}
              </dt>
              <dd className="mt-1 text-sm text-muted-foreground">{def}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-14" id="mp-faq" aria-labelledby="mp-faq-heading">
        <div className="flex flex-wrap items-center gap-2">
          <ListChecks className="size-5 text-primary" aria-hidden="true" />
          <h2 id="mp-faq-heading" className="font-display text-2xl font-bold">
            {t("mphiw.faq.heading")}
          </h2>
          <Badge variant="outline">
            {t("mphiw.faq.answers_badge", { count: content.faq.length })}
          </Badge>
        </div>
        <div className="mt-5 divide-y divide-border rounded-xl border border-border">
          {content.faq.map(([q, a], i) => (
            <details
              key={q}
              id={`mp-faq-${i + 1}`}
              open={i < 3}
              className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="cursor-pointer list-none font-display font-bold">{q}</summary>
              <p className="mt-2 text-sm text-muted-foreground">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-14" id="mp-contact" aria-labelledby="mp-contact-heading">
        <div className="flex items-center gap-2">
          <LifeBuoy className="size-5 text-primary" aria-hidden="true" />
          <h2 id="mp-contact-heading" className="font-display text-2xl font-bold">
            {t("mphiw.contact.heading")}
          </h2>
        </div>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t("mphiw.contact.body")}</p>
        <div className="mt-5">
          <ContactForm defaultTopic={t("mphiw.contact.topic")} />
        </div>
      </section>

      <section className="mt-14 text-center" aria-labelledby="mp-cta-heading">
        <Store className="mx-auto size-6 text-primary" aria-hidden="true" />
        <h2 id="mp-cta-heading" className="mt-3 font-display text-2xl font-bold">
          {t("mphiw.cta.heading")}
        </h2>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Button asChild variant="hot" size="lg">
            <Link to="/marketplace">{t("mphiw.cta.open_marketplace")}</Link>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/marketplace/sell">{t("mphiw.cta.become_seller")}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
